import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { combinedItemCount } from '../../lib/cart/cart-count-context'
import { toAddCartLineRequest } from '../cart/cart-catalog-lookup'
import { CART_MAX_LINE_QUANTITY } from '../cart/cart-limits'

type Sourceable = components['schemas']['Sourceable']

/**
 * The single shopper-facing line for UID-403's "nothing unpriced enters the cart" rule,
 * used by BOTH the client pre-flight guard and the server-rejection branch so the two
 * paths can never drift into telling the buyer two different stories.
 */
const QUOTE_REQUIRED_MESSAGE =
  'This item needs a quote before it can be added to your cart. Request a quote for it first.'

export interface AddToCartOutcome {
  /**
   * The cart's new COMBINED item count (part/BOM lines + requested
   * services — UID-423, ADR-0127 §H), for the top-bar badge
   * (`useCartCount`). Combined so the badge never contradicts itself
   * against a screen that also counts requested services (the Cart
   * page's own header subtitle, `CartServicesSection`'s count). `null`
   * means "a 401 fired — handled globally (redirect to sign-in), not
   * this call's error to surface," mirroring `CartPage.tsx`'s
   * `handleAddLine`.
   */
  readonly lineCount: number | null
}

/**
 * Add a catalog result to the cart — one unit by default (UID-253's
 * product-card inline "Add" button), or a caller-supplied quantity (UID-293's
 * Home "Copy from Excel" bulk import, which needs the pasted Qty column, not
 * a flat 1). Reuses `toAddCartLineRequest` (the same request-body builder
 * `AddLineForm`/`CartPage` use) and mirrors `CartPage.tsx`'s `handleAddLine`
 * 409-merge behavior — re-adding a SKU already in the cart bumps its
 * quantity by the requested amount rather than erroring, which is the
 * behavior a shopper expects. Kept as a standalone module (not imported from
 * `CartPage.tsx`, which has no exports) so call sites can't silently drift —
 * if `CartPage`'s merge logic changes, this one needs a deliberate matching
 * edit, not an accidental one.
 */
export async function addCatalogResultToCart(
  sourceable: Sourceable,
  quantityValue = 1,
): Promise<AddToCartOutcome> {
  // UID-403 pre-flight: a `quote_required` part has no price yet, and the server WILL
  // reject it (422 `cart/validation`) — so don't round-trip only to fail. Purely an
  // early, friendlier surface for a rule the BFF owns and enforces authoritatively
  // (cart-service.ts `addPricedLine`); this is never the security/consistency boundary.
  // Note the check is deliberately narrow: only an EXPLICIT `quote_required` mode short-
  // circuits. An absent `pricingProjection` (most catalog feeds omit it) means "unknown",
  // NOT "unpriced" — those still POST and let the server decide, because under C1 the
  // server is the price authority and the client cannot know the resolved price.
  if (sourceable.pricingProjection?.mode === 'quote_required') {
    throw new Error(QUOTE_REQUIRED_MESSAGE)
  }

  const quantity = { value: quantityValue, uom: sourceable.uom.saleUom }
  const { data, error } = await apiClient.POST('/cart/lines', {
    body: toAddCartLineRequest(sourceable, quantity),
  })
  if (data) return { lineCount: combinedItemCount(data) }

  if (error?.status === 401) return { lineCount: null } // Handled globally.

  // The server's UID-403 rejection. Its `detail` is engineer-facing (it names the ticket
  // and the resolution mechanics), so branch on the machine-readable `type` and show the
  // shopper the same friendly line the pre-flight guard uses rather than leaking it.
  if (error?.status === 422 && error.type === 'cart/validation') {
    throw new Error(QUOTE_REQUIRED_MESSAGE)
  }

  if (error?.status === 409) {
    const { data: cart } = await apiClient.GET('/cart')
    const existing = cart?.lines.find((line) => line.sourceable.id === sourceable.id)
    if (!existing) {
      throw new Error('That item is already in your cart — refresh to adjust its quantity.')
    }
    const nextValue = Math.min(CART_MAX_LINE_QUANTITY, existing.quantity.value + quantity.value)
    const patched = await apiClient.PATCH('/cart/lines/{sourceableId}', {
      params: { path: { sourceableId: sourceable.id } },
      body: { quantity: { value: nextValue, uom: existing.quantity.uom } },
    })
    if (patched.data) return { lineCount: combinedItemCount(patched.data) }
    throw new Error('Could not add that item. Please try again.')
  }

  throw new Error(error?.detail ?? 'Failed to add line')
}
